import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Music, Music2, Volume2, VolumeX, SkipForward, Play, Pause } from "lucide-react";
import { Slider } from "@/components/ui/slider";

interface MusicPlayerProps {
  selectedTrack: "lofi" | "piano" | "bollywood" | "pop";
  autoPlayCelebration?: boolean;
  onPlayStateChange?: (isPlaying: boolean) => void;
}

// Royalty-free romantic music tracks from Pixabay
const MUSIC_TRACKS = {
  lofi: {
    url: "https://cdn.pixabay.com/audio/2024/02/14/audio_8e25ba7d07.mp3",
    name: "Romantic Lo-fi",
    fallback: "https://cdn.pixabay.com/audio/2022/05/27/audio_1808fbf07a.mp3",
  },
  piano: {
    url: "https://cdn.pixabay.com/audio/2024/01/10/audio_bf7c2eb762.mp3",
    name: "Piano Serenade",
    fallback: "https://cdn.pixabay.com/audio/2022/01/18/audio_d0c6ff1bab.mp3",
  },
  bollywood: {
    url: "https://cdn.pixabay.com/audio/2023/08/10/audio_9ab2c06668.mp3",
    name: "Bollywood Love",
    fallback: "https://cdn.pixabay.com/audio/2022/10/25/audio_83267dc14c.mp3",
  },
  pop: {
    url: "https://cdn.pixabay.com/audio/2024/02/07/audio_53e04e6e92.mp3",
    name: "Pop Romance",
    fallback: "https://cdn.pixabay.com/audio/2022/03/15/audio_942b742173.mp3",
  },
};

const CELEBRATION_TRACK = "https://cdn.pixabay.com/audio/2022/03/10/audio_c8c8a73467.mp3";

const MusicPlayer = ({ selectedTrack, autoPlayCelebration, onPlayStateChange }: MusicPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.3);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const celebrationAudioRef = useRef<HTMLAudioElement | null>(null);

  // Initialize audio on mount
  useEffect(() => {
    audioRef.current = new Audio(MUSIC_TRACKS[selectedTrack].url);
    audioRef.current.loop = true;
    audioRef.current.volume = volume;

    // Handle load errors - try fallback
    audioRef.current.addEventListener("error", () => {
      if (audioRef.current) {
        audioRef.current.src = MUSIC_TRACKS[selectedTrack].fallback;
      }
    });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Handle track changes
  useEffect(() => {
    if (audioRef.current) {
      const wasPlaying = isPlaying;
      audioRef.current.pause();
      audioRef.current.src = MUSIC_TRACKS[selectedTrack].url;
      audioRef.current.volume = isMuted ? 0 : volume;
      
      if (wasPlaying) {
        audioRef.current.play().catch(() => {
          // Try fallback on error
          if (audioRef.current) {
            audioRef.current.src = MUSIC_TRACKS[selectedTrack].fallback;
            audioRef.current.play().catch(() => {});
          }
        });
      }
    }
  }, [selectedTrack]);

  // Handle celebration auto-play
  useEffect(() => {
    if (autoPlayCelebration) {
      celebrationAudioRef.current = new Audio(CELEBRATION_TRACK);
      celebrationAudioRef.current.volume = 0.4;
      celebrationAudioRef.current.play().catch(() => {});

      // After celebration, start the romantic music
      setTimeout(() => {
        if (audioRef.current && !isPlaying) {
          audioRef.current.play().catch(() => {});
          setIsPlaying(true);
          onPlayStateChange?.(true);
        }
      }, 3000);
    }
  }, [autoPlayCelebration]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlay = async () => {
    if (!audioRef.current) return;

    setIsLoading(true);
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        onPlayStateChange?.(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
        onPlayStateChange?.(true);
      }
    } catch (error) {
      // Try fallback
      if (audioRef.current) {
        audioRef.current.src = MUSIC_TRACKS[selectedTrack].fallback;
        try {
          await audioRef.current.play();
          setIsPlaying(true);
          onPlayStateChange?.(true);
        } catch {
          console.log("Music playback failed");
        }
      }
    }
    setIsLoading(false);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <motion.div
        onHoverStart={() => setShowControls(true)}
        onHoverEnd={() => setShowControls(false)}
        className="relative"
      >
        {/* Main Button */}
        <motion.button
          onClick={togglePlay}
          className={`p-4 rounded-full backdrop-blur-md border transition-all ${
            isPlaying 
              ? "bg-pink-500/30 border-pink-500/50 shadow-[0_0_20px_rgba(236,72,153,0.4)]" 
              : "bg-white/10 border-white/20 hover:bg-white/20"
          }`}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={isPlaying ? { 
            boxShadow: [
              "0 0 20px rgba(236,72,153,0.4)",
              "0 0 40px rgba(236,72,153,0.6)",
              "0 0 20px rgba(236,72,153,0.4)",
            ]
          } : {}}
          transition={{ duration: 1.5, repeat: isPlaying ? Infinity : 0 }}
        >
          {isLoading ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            >
              <Music className="w-6 h-6 text-pink-400" />
            </motion.div>
          ) : isPlaying ? (
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            >
              <Music2 className="w-6 h-6 text-pink-400" />
            </motion.div>
          ) : (
            <Music className="w-6 h-6 text-pink-400/60" />
          )}
        </motion.button>

        {/* Extended Controls */}
        <AnimatePresence>
          {showControls && (
            <motion.div
              initial={{ opacity: 0, x: 20, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 20, scale: 0.9 }}
              className="absolute bottom-0 right-16 bg-slate-900/90 backdrop-blur-md rounded-2xl p-4 border border-pink-500/20 min-w-[200px]"
            >
              {/* Track Name */}
              <p className="text-pink-300 text-sm font-medium mb-3 flex items-center gap-2">
                <Music className="w-4 h-4" />
                {MUSIC_TRACKS[selectedTrack].name}
              </p>

              {/* Play/Pause Button */}
              <div className="flex items-center gap-3 mb-3">
                <button
                  onClick={togglePlay}
                  className="p-2 rounded-full bg-pink-500/20 hover:bg-pink-500/30 transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="w-4 h-4 text-pink-400" />
                  ) : (
                    <Play className="w-4 h-4 text-pink-400" />
                  )}
                </button>
                <span className="text-pink-200 text-xs">
                  {isPlaying ? "Playing" : "Paused"}
                </span>
              </div>

              {/* Volume Control */}
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleMute}
                  className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                >
                  {isMuted ? (
                    <VolumeX className="w-4 h-4 text-pink-400" />
                  ) : (
                    <Volume2 className="w-4 h-4 text-pink-400" />
                  )}
                </button>
                <Slider
                  value={[isMuted ? 0 : volume * 100]}
                  onValueChange={(value) => {
                    setVolume(value[0] / 100);
                    if (value[0] > 0) setIsMuted(false);
                  }}
                  max={100}
                  step={1}
                  className="flex-1"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Music Visualizer Dots */}
        {isPlaying && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 flex gap-1">
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                className="w-1 bg-pink-400 rounded-full"
                animate={{
                  height: [4, 12 + Math.random() * 8, 4],
                }}
                transition={{
                  duration: 0.4,
                  delay: i * 0.1,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default MusicPlayer;
